<template>
  <div class="complaint-workbench">
    <Title title="资源问题反馈" />

    <section class="page-intro">
      <div>
        <h2>先确认问题，再决定是否修改资源</h2>
        <p>用户反馈只会进入待处理队列，不会自动隐藏、下架或改变版权策略，避免误报直接影响已发布客户端。</p>
      </div>
      <el-button icon="el-icon-refresh" :loading="loading" @click="loadList(false)">刷新</el-button>
    </section>

    <div class="status-cards">
      <button
        v-for="card in statusCards"
        :key="card.value"
        type="button"
        class="status-card"
        :class="{ active: filters.status === card.value }"
        @click="filterByStatus(card.value)">
        <span>{{ card.label }}</span>
        <strong>{{ card.count }}</strong>
        <small>{{ card.description }}</small>
      </button>
    </div>

    <el-card shadow="never" class="filter-card">
      <div class="filters">
        <el-input
          v-model.trim="filters.keyword"
          clearable
          prefix-icon="el-icon-search"
          placeholder="搜索资源名称、反馈内容或用户名"
          @keyup.enter.native="loadList()"
          @clear="loadList()" />
        <el-select v-model="filters.status" placeholder="处理状态" @change="loadList()">
          <el-option label="全部处理状态" value="" />
          <el-option
            v-for="item in options.statuses"
            :key="item.value"
            :label="item.label"
            :value="item.value" />
        </el-select>
        <el-select v-model="filters.reason_code" clearable placeholder="全部问题类型" @change="loadList()">
          <el-option
            v-for="item in options.reasons"
            :key="item.value"
            :label="item.label"
            :value="item.value" />
        </el-select>
        <el-button type="primary" icon="el-icon-search" @click="loadList()">查询</el-button>
        <el-button @click="resetFilters">重置</el-button>
      </div>
    </el-card>

    <el-card shadow="never" class="table-card">
      <el-table
        :data="list"
        v-loading="loading"
        empty-text="当前没有符合条件的资源反馈"
        style="width: 100%">
        <el-table-column label="资源" min-width="220">
          <template slot-scope="scope">
            <button type="button" class="sound-link" @click="openSoundEditor(scope.row)">
              {{ scope.row.sound_name || '资源已不存在' }}
            </button>
            <div class="secondary-text">音效 ID {{ scope.row.sound_id }}</div>
          </template>
        </el-table-column>
        <el-table-column label="问题类型" width="150">
          <template slot-scope="scope">
            <el-tag size="small" effect="plain" :type="reasonType(scope.row.reason_code)">
              {{ optionLabel(options.reasons, scope.row.reason_code, '其他问题') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="用户说明" min-width="260">
          <template slot-scope="scope">
            <div class="description-text">{{ scope.row.description || '用户未补充说明' }}</div>
            <div class="secondary-text">
              {{ scope.row.username || `用户 ${scope.row.user_id}` }}
              <span v-if="scope.row.contact"> · 联系方式：{{ scope.row.contact }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="提交时间" width="145">
          <template slot-scope="scope">{{ scope.row.created_time }}</template>
        </el-table-column>
        <el-table-column label="处理状态" width="110">
          <template slot-scope="scope">
            <el-tag size="small" :type="statusType(scope.row.status)">
              {{ optionLabel(options.statuses, scope.row.status, '待处理') }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 2026-10-11：处理意见直接显示在列表中，工作人员无需逐条打开弹窗
        才能分辨“已修复”“建议下架”或“误报”。 -->
        <el-table-column label="最终处理意见" min-width="240">
          <template slot-scope="scope">
            <template v-if="scope.row.status !== 'pending'">
              <el-tag
                size="small"
                effect="plain"
                :type="scope.row.resolution_action === 'false_report' ? 'info' : 'success'">
                {{ optionLabel(options.resolution_actions, scope.row.resolution_action, '仅记录') }}
              </el-tag>
              <div class="resolution-note">{{ scope.row.handle_note || '未填写处理备注' }}</div>
              <div class="secondary-text" v-if="scope.row.handler_name || scope.row.updated_time">
                {{ scope.row.handler_name || '工作人员' }} · {{ scope.row.updated_time }}
              </div>
            </template>
            <span v-else class="secondary-text">尚未处理</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="285" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" plain @click="openHandleDialog(scope.row)">处理</el-button>
            <el-button
              v-if="scope.row.resolution_action === 'false_report'"
              size="mini"
              type="danger"
              plain
              @click="deleteFalseReport(scope.row)">
              删除误报
            </el-button>
            <el-dropdown trigger="click" @command="handleRowCommand($event, scope.row)">
              <el-button size="mini">更多<i class="el-icon-arrow-down el-icon--right"></i></el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="edit">编辑音效</el-dropdown-item>
                <el-dropdown-item command="rights">版权与展示控制</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        background
        :current-page="page.page_num"
        :page-size="page.page_size"
        :page-sizes="[10, 20, 50]"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handlePageSizeChange"
        @current-change="handlePageChange" />
    </el-card>

    <el-dialog
      title="处理资源问题反馈"
      :visible.sync="handleDialogVisible"
      width="620px"
      :close-on-click-modal="false"
      append-to-body>
      <div v-if="currentComplaint" class="dialog-resource">
        <strong>{{ currentComplaint.sound_name }}</strong>
        <span>音效 ID {{ currentComplaint.sound_id }}</span>
        <p>{{ currentComplaint.description || '用户未补充说明' }}</p>
      </div>

      <el-alert
        title="处理建议不会自动修改资源"
        description="保存后请根据实际情况进入“编辑音效”或“版权与展示控制”完成修改。这样可以避免误报直接导致资源下架。"
        type="info"
        :closable="false"
        show-icon />

      <el-form label-position="top" class="handle-form">
        <el-form-item label="处理状态">
          <el-select v-model="handleForm.status" style="width: 100%">
            <el-option
              v-for="item in options.statuses"
              :key="item.value"
              :label="item.label"
              :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="建议动作">
          <el-select
            v-model="handleForm.resolution_action"
            style="width: 100%"
            @change="handleResolutionActionChange">
            <el-option
              v-for="item in options.resolution_actions"
              :key="item.value"
              :label="item.label"
              :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="处理备注">
          <el-input
            v-model.trim="handleForm.handle_note"
            type="textarea"
            :rows="4"
            maxlength="1000"
            show-word-limit
            placeholder="记录核实结果、处理依据或后续待办，方便其他工作人员继续跟进" />
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="handleDialogVisible = false">取消</el-button>
        <el-button
          v-if="currentComplaint"
          @click="openSoundEditor(currentComplaint)">
          编辑音效
        </el-button>
        <el-button
          v-if="currentComplaint && handleForm.resolution_action === 'false_report'"
          type="danger"
          :loading="saving"
          @click="saveAndDeleteFalseReport">
          确认误报并删除
        </el-button>
        <el-button type="primary" :loading="saving" @click="saveHandleResult">保存处理结果</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Title from '@/views/sounds/list/components/Title'
import apis from '@/api/index'
import { optionLabel } from '@/lib/soundAdminUi'

export default {
  name: 'SoundComplaintWorkbench',
  components: { Title },
  /**
   * 创建反馈工作台的筛选、分页和人工处理状态。
   *
   * @returns {Object}
   */
  data() {
    return {
      loading: false,
      saving: false,
      handleDialogVisible: false,
      currentComplaint: null,
      list: [],
      options: {
        statuses: [],
        reasons: [],
        resolution_actions: []
      },
      statusCounts: {
        pending: 0,
        processing: 0,
        resolved: 0,
        ignored: 0
      },
      filters: {
        keyword: '',
        // 2026-10-11：进入页面先处理待办；已处理记录只有主动选择“全部”或
        // 对应状态后才显示，避免完成项淹没待处理队列。
        status: 'pending',
        reason_code: ''
      },
      page: {
        page_num: 1,
        page_size: 20,
        total: 0
      },
      handleForm: {
        status: 'processing',
        resolution_action: 'none',
        handle_note: ''
      }
    }
  },
  computed: {
    /**
     * 把状态汇总转换为可点击的工作队列卡片。
     *
     * @returns {Array}
     */
    statusCards() {
      return [
        { value: 'pending', label: '待处理', count: this.statusCounts.pending, description: '建议优先核实' },
        { value: 'processing', label: '处理中', count: this.statusCounts.processing, description: '已有人员跟进' },
        { value: 'resolved', label: '已处理', count: this.statusCounts.resolved, description: '已记录处理结果' },
        { value: 'ignored', label: '已忽略', count: this.statusCounts.ignored, description: '误报或无需处理' }
      ]
    }
  },
  /**
   * 页面进入时先获取固定选项，再读取反馈列表。
   *
   * @returns {Promise<void>}
   */
  async created() {
    await this.loadOptions()
    await this.loadList(false)
  },
  methods: {
    optionLabel,
    /**
     * 获取服务端固定枚举，确保前后端使用同一组状态值。
     *
     * @returns {Promise<void>}
     */
    async loadOptions() {
      try {
        const response = await apis.soundComplaint.options()
        const payload = response && response.data ? response.data : null
        if (!payload || payload.code !== 1) {
          throw new Error((payload && payload.msg) || '反馈选项加载失败')
        }
        this.options = payload.data
      } catch (error) {
        this.$message.error(error.message || String(error))
      }
    },

    /**
     * 读取当前筛选条件下的反馈列表。
     *
     * @param {Boolean} resetPage 是否回到第一页
     * @returns {Promise<void>}
     */
    async loadList(resetPage = true) {
      if (resetPage) this.page.page_num = 1
      this.loading = true
      try {
        const response = await apis.soundComplaint.lists({
          ...this.filters,
          page_num: this.page.page_num,
          page_size: this.page.page_size
        })
        const payload = response && response.data ? response.data : null
        if (!payload || payload.code !== 1) {
          this.$message.error((payload && payload.msg) || '反馈列表加载失败')
          return
        }
        this.list = payload.data.list || []
        this.page.total = Number(payload.data.count || 0)
        this.statusCounts = {
          ...this.statusCounts,
          ...(payload.data.status_counts || {})
        }
      } catch (error) {
        this.list = []
        this.page.total = 0
        this.$message.error(error.message || String(error))
      } finally {
        this.loading = false
      }
    },

    /**
     * 返回处理状态对应的 Element 标签颜色。
     *
     * @param {String} status 处理状态
     * @returns {String}
     */
    statusType(status) {
      return {
        pending: 'warning',
        processing: '',
        resolved: 'success',
        ignored: 'info'
      }[status] || 'warning'
    },

    /**
     * 对版权和内容不适宜问题使用更醒目的颜色。
     *
     * @param {String} reasonCode 问题代码
     * @returns {String}
     */
    reasonType(reasonCode) {
      if (reasonCode === 'copyright' || reasonCode === 'inappropriate') return 'danger'
      if (reasonCode === 'play_or_download') return 'warning'
      return 'info'
    },

    /**
     * 点击状态卡时只切换到对应队列，不因重复点击意外变成“全部”。
     * 查看全部记录必须由工作人员在筛选框中主动选择。
     *
     * @param {String} status 状态值
     * @returns {void}
     */
    filterByStatus(status) {
      this.filters.status = status
      this.loadList()
    },

    /**
     * 清空筛选并重新读取第一页。
     *
     * @returns {void}
     */
    resetFilters() {
      this.filters = { keyword: '', status: 'pending', reason_code: '' }
      this.loadList()
    },

    /**
     * 打开人工处理弹窗，并保留已有处理内容方便继续跟进。
     *
     * @param {Object} row 反馈记录
     * @returns {void}
     */
    openHandleDialog(row) {
      this.currentComplaint = row
      this.handleForm = {
        status: row.status === 'pending' ? 'processing' : row.status,
        resolution_action: row.resolution_action || 'none',
        handle_note: row.handle_note || ''
      }
      this.handleDialogVisible = true
    },

    /**
     * “误报”是最终结论，选择后同步把处理状态改为“已忽略”。
     *
     * 服务端仍会再次校正状态，防止旧页面或手工请求保存出互相矛盾的数据。
     *
     * @param {String} action 处理意见编码
     * @returns {void}
     */
    handleResolutionActionChange(action) {
      if (action === 'false_report') {
        this.handleForm.status = 'ignored'
      } else if (this.handleForm.status === 'ignored') {
        // 从“误报”改回其他处理意见时，默认恢复成“已处理”，避免留下
        // “建议修正信息但状态仍是已忽略”这种容易误解的组合。
        this.handleForm.status = 'resolved'
      }
    },

    /**
     * 保存处理记录。此操作只改变反馈状态，不直接操作音效展示。
     *
     * @returns {Promise<void>}
     */
    async saveHandleResult() {
      if (!this.currentComplaint || this.saving) return
      this.saving = true
      try {
        const response = await apis.soundComplaint.update({
          id: this.currentComplaint.id,
          ...this.handleForm
        })
        const payload = response && response.data ? response.data : null
        if (!payload || payload.code !== 1) {
          this.$message.error((payload && payload.msg) || '保存失败')
          return
        }
        this.$message.success('处理结果已保存')
        this.handleDialogVisible = false
        await this.loadList(false)
      } catch (error) {
        this.$message.error(error.message || String(error))
      } finally {
        this.saving = false
      }
    },

    /**
     * 在处理弹窗中一次完成“标记误报”和“删除”。
     *
     * 实际请求仍然先保存误报结论，再调用只允许删除误报的接口。第二步失败时
     * 反馈会安全地保留为误报，工作人员之后可在“全部处理状态”中再次删除。
     *
     * @returns {Promise<void>}
     */
    async saveAndDeleteFalseReport() {
      if (!this.currentComplaint || this.handleForm.resolution_action !== 'false_report' || this.saving) return
      try {
        await this.$confirm(
          `确定把“${this.currentComplaint.sound_name || '未知资源'}”的这条反馈认定为误报并永久删除吗？`,
          '确认误报并删除',
          {
            confirmButtonText: '确认删除',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(error.message || String(error))
        }
        return
      }

      this.saving = true
      try {
        const updateResponse = await apis.soundComplaint.update({
          id: this.currentComplaint.id,
          ...this.handleForm,
          status: 'ignored',
          resolution_action: 'false_report'
        })
        const updatePayload = updateResponse && updateResponse.data ? updateResponse.data : null
        if (!updatePayload || updatePayload.code !== 1) {
          this.$message.error((updatePayload && updatePayload.msg) || '误报结论保存失败')
          return
        }

        const removeResponse = await apis.soundComplaint.remove({ id: this.currentComplaint.id })
        const removePayload = removeResponse && removeResponse.data ? removeResponse.data : null
        if (!removePayload || removePayload.code !== 1) {
          this.$message.error(`已保存为误报，但删除失败：${(removePayload && removePayload.msg) || '请稍后重试'}`)
          this.handleDialogVisible = false
          await this.loadList(false)
          return
        }

        this.$message.success('误报反馈已删除')
        this.handleDialogVisible = false
        await this.loadList(false)
      } catch (error) {
        this.$message.error(error.message || String(error))
      } finally {
        this.saving = false
      }
    },

    /**
     * 永久删除已核实为误报的反馈。删除前必须二次确认；服务端还会再次检查
     * resolution_action，普通反馈即使绕过页面也无法被此接口删除。
     *
     * @param {Object} row 反馈记录
     * @returns {Promise<void>}
     */
    async deleteFalseReport(row) {
      if (!row || row.resolution_action !== 'false_report') return
      try {
        await this.$confirm(
          `确定删除“${row.sound_name || '未知资源'}”的这条误报反馈吗？删除后不能恢复。`,
          '删除误报反馈',
          {
            confirmButtonText: '确认删除',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        const response = await apis.soundComplaint.remove({ id: row.id })
        const payload = response && response.data ? response.data : null
        if (!payload || payload.code !== 1) {
          this.$message.error((payload && payload.msg) || '删除失败')
          return
        }
        this.$message.success('误报反馈已删除')
        if (this.currentComplaint && this.currentComplaint.id === row.id) {
          this.handleDialogVisible = false
        }
        await this.loadList(false)
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error(error.message || String(error))
        }
      }
    },

    /**
     * 进入现有音效编辑页，避免在反馈模块重复实现资源编辑能力。
     *
     * @param {Object} row 反馈记录
     * @returns {void}
     */
    openSoundEditor(row) {
      this.$router.push({ name: 'updateSound', query: { id: row.sound_id } })
    },

    /**
     * 进入现有版权工作台，并通过查询参数定位对应资源。
     *
     * @param {Object} row 反馈记录
     * @returns {void}
     */
    openRightsWorkbench(row) {
      this.$router.push({ name: 'soundRights', query: { sound_id: row.sound_id } })
    },

    /**
     * 处理表格“更多”菜单命令。
     *
     * @param {String} command 菜单命令
     * @param {Object} row 反馈记录
     * @returns {void}
     */
    handleRowCommand(command, row) {
      if (command === 'rights') {
        this.openRightsWorkbench(row)
        return
      }
      this.openSoundEditor(row)
    },

    /**
     * 调整每页数量并回到第一页。
     *
     * @param {Number} size 每页数量
     * @returns {void}
     */
    handlePageSizeChange(size) {
      this.page.page_size = size
      this.loadList()
    },

    /**
     * 切换分页并保留当前筛选条件。
     *
     * @param {Number} pageNum 页码
     * @returns {void}
     */
    handlePageChange(pageNum) {
      this.page.page_num = pageNum
      this.loadList(false)
    }
  }
}
</script>

<style lang="scss" scoped>
.complaint-workbench {
  padding: 20px;
  background: #f4f7fb;
  min-height: calc(100vh - 84px);
  color: #24324a;
}

.page-intro {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  margin: 18px 0;
  padding: 22px 26px;
  background: linear-gradient(135deg, #eef6ff, #fff);
  border: 1px solid #d9e9fb;
  border-radius: 14px;

  h2 {
    margin: 0 0 8px;
    font-size: 20px;
  }

  p {
    margin: 0;
    color: #66758c;
    line-height: 1.7;
  }
}

.status-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.status-card {
  padding: 16px 18px;
  text-align: left;
  border: 1px solid #e0e7f1;
  border-radius: 12px;
  background: #fff;
  color: #627087;
  cursor: pointer;
  transition: border-color .2s, box-shadow .2s, transform .2s;

  &:hover,
  &.active {
    border-color: #409eff;
    box-shadow: 0 8px 20px rgba(64, 158, 255, .12);
    transform: translateY(-1px);
  }

  span,
  small {
    display: block;
  }

  strong {
    display: block;
    margin: 8px 0 5px;
    color: #1f2d45;
    font-size: 28px;
  }
}

.filter-card,
.table-card {
  margin-bottom: 14px;
  border: 0;
  border-radius: 12px;
}

.filters {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 180px 190px auto auto;
  gap: 10px;
}

.sound-link {
  max-width: 100%;
  padding: 0;
  overflow: hidden;
  border: 0;
  background: transparent;
  color: #2d5f9d;
  font-weight: 600;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.description-text {
  line-height: 1.55;
  color: #2d3b52;
  word-break: break-word;
}

.secondary-text {
  margin-top: 5px;
  color: #8a97aa;
  font-size: 12px;
}

.resolution-note {
  margin-top: 7px;
  color: #45536a;
  line-height: 1.45;
  word-break: break-word;
}

.pagination {
  margin-top: 18px;
  text-align: right;
}

.dialog-resource {
  margin-bottom: 14px;
  padding: 14px 16px;
  background: #f6f8fb;
  border-radius: 10px;

  strong,
  span {
    display: block;
  }

  span {
    margin-top: 4px;
    color: #8a97aa;
    font-size: 12px;
  }

  p {
    margin: 10px 0 0;
    line-height: 1.6;
  }
}

.handle-form {
  margin-top: 18px;
}

@media (max-width: 1100px) {
  .status-cards {
    grid-template-columns: repeat(2, 1fr);
  }

  .filters {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
